import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileText, X, Play, CheckCircle, File, Calendar, AlertTriangle, Clock, Save, LogOut, Sun, Moon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './index.css';
import { useAuth } from './auth/AuthContext';
import { useTheme } from './hooks/useTheme';
import MiraWidget from './components/MiraWidget';
import { Button, Tabs } from './components/common';
import SummaryView from './components/views/SummaryView';
import NegotiationView from './components/views/NegotiationView';
import ScopeView from './components/views/ScopeView';
import HistoryView from './components/views/HistoryView';
import ExclusionsView from './components/views/ExclusionsView';
import EmailView from './components/views/EmailView';

// --- Main App ---

export default function App() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [activeTab, setActiveTab] = useState('chat');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [taskType, setTaskType] = useState("CONTRACT_REVIEW");
  const [isNewClient, setIsNewClient] = useState(false);  // New Client checkbox state
  
  // State for document management - filtered by current session
  const [contracts, setContracts] = useState([]);  // Contract documents for current session
  const [proposals, setProposals] = useState([]);  // Proposal documents for current session
  const [activeContract, setActiveContract] = useState(null);
  const [activeProposal, setActiveProposal] = useState(null);
  
  // History state
  const [reviewHistory, setReviewHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [loadedReviewId, setLoadedReviewId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const backendUrl = ""; // Use relative path for Ingress routing

  // Load documents for current session and history on mount
  useEffect(() => {
    createNewSession();
  }, []);

  // Load documents when sessionId changes
  useEffect(() => {
    if (sessionId) {
      loadDocuments(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Publish lightweight app state for Mira telemetry (no PII, no doc contents)
  useEffect(() => {
    window.__abs_app_state__ = {
      active_tab: activeTab,
      session_id: sessionId,
      task_type: taskType,
      is_new_client: isNewClient,
      contract_count: contracts.length,
      proposal_count: proposals.length,
      active_contract: activeContract?.filename || null,
      active_proposal: activeProposal?.filename || null,
      has_analysis_result: !!analysisResult,
      is_loading: isLoading,
      theme: theme,
    };
  }, [activeTab, sessionId, taskType, isNewClient, contracts.length, proposals.length, activeContract, activeProposal, analysisResult, isLoading, theme]);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadDocuments = async (sid) => {
    try {
      const url = sid ? `${backendUrl}/api/documents?session_id=${sid}` : `${backendUrl}/api/documents`;
      const res = await fetch(url);
      const docs = await res.json();
      
      const contractDocs = docs.filter(d => d.document_type === 'contract');
      const proposalDocs = docs.filter(d => d.document_type === 'proposal');
      
      setContracts(contractDocs);
      setProposals(proposalDocs);
      
      // Set active documents
      const activeC = contractDocs.find(d => d.is_active);
      const activeP = proposalDocs.find(d => d.is_active);
      setActiveContract(activeC || null);
      setActiveProposal(activeP || null);
    } catch (err) {
      console.error("Failed to load documents", err);
    }
  };

  const createNewSession = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_type: taskType })
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages([{ role: 'assistant', content: `Greetings and salutations, I am your Contract Administrator. Upload your contract and proposal to begin review. Remember, stay hungry my friends and always ask calibrated questions.` }]);
      setAnalysisResult(null);
      setLoadedReviewId(null);
      // Clear documents for new session
      setContracts([]);
      setProposals([]);
      setActiveContract(null);
      setActiveProposal(null);
      // Reset processing states
      setIsProcessing(false);
      setProcessingQueue([]);
      setProcessingStats({ completed: 0, total: 0 });
      setProcessingStatus({ stage: '', filename: '', progress: 0, message: '' });
    } catch (err) {
      console.error("Failed to create session", err);
    }
  };

  // Extract shortened project name from contract filename (number and name only)
  const extractProjectName = (filename) => {
    if (!filename) return null;
    // Remove file extension
    let name = filename.replace(/\.(pdf|docx|doc)$/i, '');
    // Try to extract project number and name (e.g., "18426 Grand County EMS" from "18426 Grand County EMS - ABS Subcontract - Received.pdf")
    const match = name.match(/^(\d+)\s+([^-–]+)/);
    if (match) {
      return `${match[1]} ${match[2].trim()}`;
    }
    // If no number found, just return first part before dash
    const firstPart = name.split(/[-–]/)[0].trim();
    return firstPart || name;
  };

  // History functions
  const loadHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/contract-reviews?limit=50`);
      const data = await res.json();
      setReviewHistory(data.reviews || []);
    } catch (err) {
      console.error("Failed to load history", err);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const saveCurrentReview = async () => {
    if (!sessionId) return;
    
    // Auto-generate project name from contract filename
    const autoProjectName = activeContract ? extractProjectName(activeContract.filename) : null;
    const projectName = autoProjectName || analysisResult?.structured_data?.summary_data?.project_name || 'Untitled Review';
    
    try {
      const res = await fetch(`${backendUrl}/api/contract-reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          project_name: projectName,
          messages: messages,
          summary_data: analysisResult?.structured_data?.summary_data,
          negotiation_summary: analysisResult?.structured_data?.negotiation_summary,
          scope_data: analysisResult?.structured_data?.scope_data,
          analysis_result: analysisResult,
          task_type: taskType
        })
      });
      const data = await res.json();
      console.log("Review saved:", data);
      // Refresh history
      loadHistory();
      return data;
    } catch (err) {
      console.error("Failed to save review", err);
    }
  };

  // Save current review and start a new one
  const saveAndNewReview = async () => {
    // Only save if there's something to save (has analysis or documents)
    if (analysisResult || activeContract || activeProposal) {
      await saveCurrentReview();
    }
    
    // Clear active documents in the database
    try {
      await fetch(`${backendUrl}/api/documents/clear-active`, {
        method: 'POST'
      });
    } catch (err) {
      console.error("Failed to clear documents", err);
    }
    
    // Reset all processing states
    setIsProcessing(false);
    setProcessingQueue([]);
    setProcessingStats({ completed: 0, total: 0 });
    setProcessingStatus({ 
      stage: '', 
      filename: '',
      progress: 0,
      message: ''
    });
    
    // Reset file inputs so they can accept new files
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.value = '';
    });
    
    // Create new session
    await createNewSession();
  };

  const loadReviewFromHistory = async (reviewId) => {
    try {
      const res = await fetch(`${backendUrl}/api/contract-reviews/${reviewId}`);
      const review = await res.json();
      
      // Load the review data into state
      setLoadedReviewId(reviewId);
      setMessages(review.messages || []);
      
      // Reconstruct analysis result
      if (review.summary_data || review.negotiation_summary || review.scope_data) {
        setAnalysisResult({
          structured_data: {
            summary_data: review.summary_data,
            negotiation_summary: review.negotiation_summary,
            scope_data: review.scope_data,
            exclusions_data: review.analysis_result?.structured_data?.exclusions_data
          },
          ...review.analysis_result
        });
      }
      
      // Set task type if available
      if (review.task_type) {
        setTaskType(review.task_type);
      }
      
      // Load documents associated with the review
      setContracts(review.contracts || []);
      setProposals(review.proposals || []);
      
      // Set active documents
      const activeC = (review.contracts || []).find(d => d.is_active);
      const activeP = (review.proposals || []).find(d => d.is_active);
      setActiveContract(activeC || null);
      setActiveProposal(activeP || null);
      
      // Switch to chat tab
      setActiveTab('chat');
      
      // Show notification
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: `Loaded contract review: **${review.project_name || 'Untitled'}** (${review.days_remaining} days remaining)` 
      }]);
      
    } catch (err) {
      console.error("Failed to load review", err);
    }
  };

  const deleteReviewFromHistory = async (reviewId) => {
    // Confirm deletion
    if (!window.confirm("Delete this contract review?")) {
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/contract-reviews/${reviewId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`);
      }
      
      // Refresh history after successful delete
      await loadHistory();
    } catch (err) {
      console.error("Failed to delete review", err);
      alert("Failed to delete review. Please try again.");
    }
  };

  // Auto-save review when analysis completes
  useEffect(() => {
    if (analysisResult && sessionId) {
      saveCurrentReview();
    }
  }, [analysisResult]);

  const [processingQueue, setProcessingQueue] = useState([]);
  const [processingStats, setProcessingStats] = useState({ completed: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState({ 
    stage: '', // 'uploading', 'extracting', 'analyzing', 'complete', 'error'
    filename: '',
    progress: 0,
    message: ''
  });

  useEffect(() => {
    if (processingQueue.length > 0 && !isProcessing) {
        processNextInQueue();
    }
  }, [processingQueue, isProcessing]);

  const processNextInQueue = async () => {
      setIsProcessing(true);
      const currentFile = processingQueue[0];
      const { file, taskType, documentType } = currentFile;

      // Update processing status (left pane only)
      setProcessingStatus({
        stage: 'uploading',
        filename: file.name,
        progress: 10,
        message: `Uploading ${file.name}...`
      });

      try {
          // 1. Upload with document type and session_id
          setProcessingStatus(prev => ({ ...prev, stage: 'uploading', progress: 20, message: `Uploading ${file.name}...` }));
          
          const formData = new FormData();
          formData.append('file', file);
          
          const uploadUrl = sessionId 
            ? `${backendUrl}/api/upload?document_type=${documentType}&session_id=${sessionId}`
            : `${backendUrl}/api/upload?document_type=${documentType}`;
          
          const uploadRes = await fetch(uploadUrl, {
              method: 'POST',
              body: formData
          });
          
          if (!uploadRes.ok) {
              const err = await uploadRes.json().catch(() => ({}));
              throw new Error(err.detail || `Upload failed: ${uploadRes.status}`);
          }
          
          setProcessingStatus(prev => ({ ...prev, stage: 'extracting', progress: 40, message: `Extracting text from ${file.name}...` }));
          
          const uploadData = await uploadRes.json();
          
          // Reload documents for current session
          await loadDocuments(sessionId);
          
          setProcessingStatus(prev => ({ ...prev, progress: 50, message: `Document uploaded successfully` }));

          // 2. Attach to session
          if (sessionId) {
              await fetch(`${backendUrl}/api/sessions/${sessionId}/attach`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ file_id: uploadData.file_id })
              });

              // 3. Analyze ONLY for contracts, NOT for proposals
              // Proposals are only used for the Scope tab comparison
              if (documentType === "contract") {
                  setProcessingStatus(prev => ({ ...prev, stage: 'analyzing', progress: 60, message: `Analyzing ${file.name}...` }));
                  await runAnalysis(uploadData.file_id, taskType, file.name);
                  setProcessingStatus(prev => ({ ...prev, stage: 'complete', progress: 100, message: `Completed ${file.name}` }));
              } else {
                  // Proposal uploaded - ALWAYS extract exclusions first
                  setProcessingStatus(prev => ({ ...prev, stage: 'analyzing', progress: 60, message: `Extracting exclusions from ${file.name}...` }));
                  
                  // Run PROPOSAL_EXCLUSIONS to extract exclusions immediately
                  try {
                      const exclusionsRes = await fetch(`${backendUrl}/api/analyze`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ file_id: uploadData.file_id, task_type: "PROPOSAL_EXCLUSIONS" })
                      });
                      
                      if (exclusionsRes.ok) {
                          const exclusionsResult = await exclusionsRes.json();
                          // Merge exclusions_data into analysis result
                          setAnalysisResult(prev => ({
                              ...prev,
                              structured_data: {
                                  ...prev?.structured_data,
                                  exclusions_data: exclusionsResult?.structured_data?.exclusions_data
                              }
                          }));
                          
                          const totalExclusions = (exclusionsResult?.structured_data?.exclusions_data?.general_exclusions?.length || 0) +
                              (exclusionsResult?.structured_data?.exclusions_data?.exclusions_by_scope?.reduce((sum, g) => sum + (g.exclusions?.length || 0), 0) || 0);
                          
                          setMessages(prev => [...prev, { 
                            role: 'assistant', 
                            content: `**Proposal uploaded:** ${file.name}\n\n**${totalExclusions} exclusions extracted.** See the Exclusions tab for details.` 
                          }]);
                      }
                  } catch (exclusionsErr) {
                      console.error("Exclusions extraction error:", exclusionsErr);
                  }
                  
                  // Then check if contract exists and auto-run scope review
                  if (activeContract) {
                      // Contract exists - auto-run scope review
                      setProcessingStatus(prev => ({ ...prev, stage: 'analyzing', progress: 80, message: `Running Scope Review...` }));
                      setMessages(prev => [...prev, { 
                        role: 'assistant', 
                        content: `Contract detected. Automatically running Scope Review...` 
                      }]);
                      
                      // Run scope review with the proposal
                      try {
                          const scopeRes = await fetch(`${backendUrl}/api/analyze`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ file_id: uploadData.file_id, task_type: "SCOPE_REVIEW" })
                          });
                          
                          if (scopeRes.ok) {
                              const scopeResult = await scopeRes.json();
                              // MERGE scope data AND exclusions_data with existing analysis result - DO NOT replace summary/terms
                              setAnalysisResult(prev => ({
                                  ...prev,
                                  structured_data: {
                                      ...prev?.structured_data,
                                      scope_data: scopeResult?.structured_data?.scope_data || scopeResult?.structured_data,
                                      exclusions_data: scopeResult?.structured_data?.exclusions_data || prev?.structured_data?.exclusions_data
                                  }
                              }));
                              setActiveTab('scope');
                              const scopeStatus = scopeResult?.structured_data?.scope_data?.scope_review_status || 
                                                  scopeResult?.structured_data?.scope_review_status || "Review Complete";
                              setMessages(prev => [...prev, { 
                                role: 'assistant', 
                                content: `**Scope Review Complete**\n\nStatus: ${scopeStatus}\n\nSee the Scope tab for details.` 
                              }]);
                          }
                      } catch (scopeErr) {
                          console.error("Auto scope review error:", scopeErr);
                      }

                      // Auto-refresh the Material Deposits row in Terms + Summary Yes/No flag.
                      try {
                          const depRes = await fetch(`${backendUrl}/api/refresh-deposits`, { method: 'POST' });
                          if (depRes.ok) {
                              const depJson = await depRes.json();
                              const newRow = depJson?.deposits_row;  // null when no deposits
                              const detected = depJson?.detected || [];
                              const mdFlag = depJson?.material_deposits_required || (newRow ? 'Yes' : 'No');

                              setAnalysisResult(prev => {
                                  const existing = prev?.structured_data?.negotiation_summary || [];
                                  const idx = existing.findIndex(r => {
                                      const t = (r?.title || '').toLowerCase();
                                      return t.includes('material deposit') || t.includes('deposit');
                                  });
                                  let next = [...existing];
                                  if (newRow) {
                                      if (idx >= 0) next[idx] = newRow;
                                      else next.push(newRow);
                                  } else if (idx >= 0) {
                                      // No deposits detected — drop any stale deposit row
                                      next = next.filter((_, i) => i !== idx);
                                  }
                                  const prevSummary = prev?.structured_data?.summary_data || {};
                                  return {
                                      ...prev,
                                      structured_data: {
                                          ...prev?.structured_data,
                                          negotiation_summary: next,
                                          summary_data: { ...prevSummary, material_deposits_required: mdFlag },
                                      },
                                  };
                              });

                              if (newRow && detected.length > 0) {
                                  setMessages(prev => [...prev, {
                                      role: 'assistant',
                                      content: `**Material Deposits updated** in Terms — ${detected.length} deposit reference${detected.length === 1 ? '' : 's'} captured from the proposal.`,
                                  }]);
                              }
                          }
                      } catch (depErr) {
                          console.error("Refresh deposits error:", depErr);
                      }

                      setProcessingStatus(prev => ({ ...prev, stage: 'complete', progress: 100, message: `Scope Review Complete` }));
                  } else {
                      // No contract yet - just show exclusions extracted
                      setProcessingStatus(prev => ({ ...prev, stage: 'complete', progress: 100, message: `Exclusions extracted from ${file.name}` }));
                      setActiveTab('exclusions');
                  }
              }
          }

      } catch (err) {
          console.error(`Error processing ${file.name}`, err);
          setProcessingStatus(prev => ({ ...prev, stage: 'error', progress: 0, message: err.message || 'Processing failed' }));
          // Only show error in chat
          setMessages(prev => [...prev, { role: 'assistant', content: `Error processing **${file.name}**: ${err.message || 'Please try again.'}` }]);
      } finally {
          // Cleanup
          setProcessingStats(prev => ({ ...prev, completed: prev.completed + 1 }));
          setProcessingQueue(prev => prev.slice(1));
          setIsProcessing(false);
          
          // Clear status after a delay if queue is empty
          if (processingQueue.length <= 1) {
            setTimeout(() => {
              setProcessingStatus({ stage: '', filename: '', progress: 0, message: '' });
            }, 2000);
          }
      }
  };

  // Handle Contract upload (from "Upload Contract" control)
  const handleContractUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    // Reset stats if starting a new batch from idle
    if (processingQueue.length === 0) {
        setProcessingStats({ completed: 0, total: selectedFiles.length });
    } else {
        setProcessingStats(prev => ({ ...prev, total: prev.total + selectedFiles.length }));
    }

    // Add to queue with document_type = "contract"
    const newQueueItems = selectedFiles.map(file => ({
        file,
        taskType,
        documentType: "contract"
    }));
    
    setProcessingQueue(prev => [...prev, ...newQueueItems]);
  };

  // Handle Proposal upload (from "Upload Proposal" control)
  const handleProposalUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    // Reset stats if starting a new batch from idle
    if (processingQueue.length === 0) {
        setProcessingStats({ completed: 0, total: selectedFiles.length });
    } else {
        setProcessingStats(prev => ({ ...prev, total: prev.total + selectedFiles.length }));
    }

    // Add to queue with document_type = "proposal"
    const newQueueItems = selectedFiles.map(file => ({
        file,
        taskType,
        documentType: "proposal"
    }));
    
    setProcessingQueue(prev => [...prev, ...newQueueItems]);
  };

  // Set a document as active
  const setDocumentActive = async (fileId, documentType) => {
    try {
      await fetch(`${backendUrl}/api/documents/set-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_id: fileId, document_type: documentType })
      });
      await loadDocuments();
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: `Set ${documentType} as active.` 
      }]);
    } catch (err) {
      console.error("Failed to set active document", err);
    }
  };

  // Delete a document
  const deleteDocument = async (fileId, documentType) => {
    try {
      await fetch(`${backendUrl}/api/documents/${fileId}`, {
        method: 'DELETE'
      });
      await loadDocuments();
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: `Deleted ${documentType} document.` 
      }]);
    } catch (err) {
      console.error("Failed to delete document", err);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'America/Denver'
    });
  };

  const runAnalysis = async (fileId, type, filename, retries = 3) => {
    // Queue manages loading state - no direct setIsLoading call needed here
    
    for (let attempt = 0; attempt < retries; attempt++) {
        try {
            const payload = { file_id: fileId, task_type: type };

            const res = await fetch(`${backendUrl}/api/analyze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!res.ok) {
                // Read body as text ONCE (avoids 'body stream already read' errors)
                const bodyText = await res.text().catch(() => '');
                let errorDetail = bodyText;
                try {
                    const parsed = JSON.parse(bodyText);
                    errorDetail = parsed.detail || bodyText || `Server error: ${res.status}`;
                } catch {
                    // body is not JSON (e.g., HTML error page) — use raw text
                    errorDetail = bodyText || `Server error: ${res.status}`;
                }
                // Non-retriable: 4xx including 402 (Payment Required)
                if (res.status === 402 || (res.status >= 400 && res.status < 500)) {
                    throw new Error(errorDetail);
                }
                // Retriable: 5xx — throw to trigger retry
                throw new Error(`Server error ${res.status}: ${errorDetail}`);
            }
            
            const result = await res.json();
            
            // Check if schedule was extracted
            let scheduleMsg = "";
            if (result.schedule_pdf) {
                scheduleMsg = `\n\nI extracted the **Project Schedule** for ${filename} into a separate PDF.`;
            }
            
            // Add summary message
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: `**${filename}**: Analysis Complete! ${scheduleMsg} Check the Report tab for details.` 
            }]);
            
            // Update result view only if it's the latest
            setAnalysisResult(result);
            setActiveTab('summary'); // Default to Summary tab
            return; // Success, exit loop
            
        } catch (err) {
            console.warn(`Analysis attempt ${attempt + 1} failed:`, err);
            // If it's the last attempt or a non-retriable error (handled above), rethrow
            if (attempt === retries - 1 || err.message.includes("Payment Required") || err.message.includes("Quota Exceeded")) {
                throw err;
            }
            // Wait before retry (exponential backoff)
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
    }
  };

  // Run Scope Review specifically - triggered from Scope tab
  const runScopeReview = async () => {
    if (!activeProposal) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `**Scope Review:** Please upload a Proposal document first. The Proposal defines what ABS priced and is required for scope review.` 
      }]);
      return;
    }

    setIsLoading(true);
    setActiveTab('scope');
    
    try {
      const payload = { 
        file_id: activeProposal.file_id, 
        task_type: "SCOPE_REVIEW" 
      };

      const res = await fetch(`${backendUrl}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${res.status}`);
      }
      
      const result = await res.json();
      
      // MERGE scope data AND exclusions_data with existing analysis result - DO NOT replace summary/terms
      setAnalysisResult(prev => ({
          ...prev,
          structured_data: {
              ...prev?.structured_data,
              scope_data: result?.structured_data?.scope_data || result?.structured_data,
              exclusions_data: result?.structured_data?.exclusions_data || prev?.structured_data?.exclusions_data
          }
      }));
      
      // Add message based on result
      const scopeStatus = result?.structured_data?.scope_data?.scope_review_status || 
                          result?.structured_data?.scope_review_status || 
                          "Review Complete";
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `**Scope Review Complete**\n\nStatus: ${scopeStatus}\n\nSee the Scope tab for details.` 
      }]);
      
    } catch (err) {
      console.error("Scope review error:", err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `**Scope Review Error:** ${err.message || 'Please try again.'}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !sessionId) return;
    
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      const res = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: userMsg.content })
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I encountered an error." }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${isDark ? 'dark ' : ''}flex flex-col h-screen bg-[hsl(var(--neutral-100))] font-sans text-gray-900`}>

      {/* Full-width Top Header */}
      <header className="bg-black flex items-center shadow-sm z-10 flex-shrink-0" data-testid="app-header">
          {/* Brand: white logo panel (kept light in both themes so the ABS mark stays legible) */}
          <div className="bg-[#ffffff] px-4 py-3 flex items-center gap-3 flex-shrink-0 self-stretch">
              <img
                src={`${process.env.PUBLIC_URL || ''}/abs-logo.png`}
                alt="ABS Logo"
                className="h-7 w-auto"
                data-testid="header-logo"
              />
              <div className="h-6 w-px bg-[#d1d5db]" data-testid="header-divider" />
              <span className="font-semibold text-[#111827] text-sm whitespace-nowrap" data-testid="header-app-name">
                Contracts
              </span>
          </div>

          {/* Tabs (black bar) */}
          <div className="flex items-center min-w-0 overflow-x-auto ml-4 py-2">
              <Tabs
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  tabs={[
                      {id: 'chat', label: 'Chat'},
                      {id: 'summary', label: 'Summary'},
                      {id: 'terms', label: 'Terms'},
                      {id: 'scope', label: 'Scope'},
                      {id: 'exclusions', label: 'Exclusions'},
                      {id: 'email', label: 'Email'},
                      {id: 'history', label: 'History'}
                  ]}
              />
          </div>

          {/* Theme toggle + user + sign out pushed to far right */}
          <div className="ml-auto flex items-center gap-3 flex-shrink-0 pr-4" data-testid="header-user-block">
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors"
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                data-testid="theme-toggle-btn"
              >
                  {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <span className="hidden md:inline text-sm text-white/70 truncate max-w-[220px]" data-testid="header-user-email">
                {user?.email || user?.name || 'User'}
              </span>
              <button
                onClick={logout}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors"
                title="Sign out"
                data-testid="header-signout-btn"
              >
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Sign out</span>
              </button>
          </div>
      </header>

      {/* Body row: locked sidebar + main content */}
      <div className="flex flex-1 min-h-0">

      {/* Sidebar — always visible, right border continues the header line */}
      <div className="w-56 bg-white border-r border-gray-200 flex flex-col flex-shrink-0" data-testid="sidebar">
        <div className="p-3 flex-1 overflow-y-auto">
            <Button variant="outline" className="w-full justify-start gap-2 mb-3 px-2 text-xs bg-green-50 hover:bg-green-100 border-green-300 text-green-700" onClick={saveAndNewReview}>
                <Save size={14} /> Save & New Review
            </Button>
            
            {/* Live Processing Status Bar */}
            {(isProcessing || processingStatus.stage) && (
                <div className={`mb-4 p-3 rounded-lg border ${
                    processingStatus.stage === 'error' 
                        ? 'bg-red-50 border-red-200' 
                        : processingStatus.stage === 'complete'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-blue-50 border-blue-200'
                }`}>
                    {/* Stage indicator with animated icon */}
                    <div className="flex items-center gap-2 mb-2">
                        {processingStatus.stage === 'uploading' && (
                            <Upload size={14} className="text-blue-600 animate-bounce" />
                        )}
                        {processingStatus.stage === 'extracting' && (
                            <FileText size={14} className="text-blue-600 animate-pulse" />
                        )}
                        {processingStatus.stage === 'analyzing' && (
                            <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        )}
                        {processingStatus.stage === 'complete' && (
                            <CheckCircle size={14} className="text-green-600" />
                        )}
                        {processingStatus.stage === 'error' && (
                            <AlertTriangle size={14} className="text-red-600" />
                        )}
                        <span className={`text-xs font-semibold uppercase ${
                            processingStatus.stage === 'error' ? 'text-red-700' :
                            processingStatus.stage === 'complete' ? 'text-green-700' :
                            'text-blue-700'
                        }`}>
                            {processingStatus.stage === 'uploading' && 'Uploading'}
                            {processingStatus.stage === 'extracting' && 'Extracting Text'}
                            {processingStatus.stage === 'analyzing' && 'Analyzing'}
                            {processingStatus.stage === 'complete' && 'Complete'}
                            {processingStatus.stage === 'error' && 'Error'}
                        </span>
                    </div>
                    
                    {/* Filename */}
                    <div className="text-xs text-gray-600 truncate mb-2">
                        {processingStatus.filename}
                    </div>
                    
                    {/* Progress bar with animated gradient */}
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                                processingStatus.stage === 'error' 
                                    ? 'bg-red-500' 
                                    : processingStatus.stage === 'complete'
                                    ? 'bg-green-500'
                                    : 'bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 bg-[length:200%_100%] animate-shimmer'
                            }`}
                            style={{ width: `${processingStatus.progress}%` }}
                        ></div>
                    </div>
                    
                    {/* Status message */}
                    <div className={`text-xs mt-2 ${
                        processingStatus.stage === 'error' ? 'text-red-600' :
                        processingStatus.stage === 'complete' ? 'text-green-600' :
                        'text-blue-600'
                    }`}>
                        {processingStatus.message}
                    </div>
                    
                    {/* File count if multiple */}
                    {processingStats.total > 1 && (
                        <div className="flex justify-between text-[10px] text-gray-500 mt-2 pt-2 border-t border-gray-200">
                            <span>File {processingStats.completed + 1} of {processingStats.total}</span>
                            <span>{Math.round((processingStats.completed / processingStats.total) * 100)}% overall</span>
                        </div>
                    )}
                </div>
            )}

            {/* CONTRACT DOCUMENTS SECTION */}
            <div className="mb-4">
                <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider flex items-center gap-2">
                    <FileText size={12} className="text-blue-600" />
                    Contract Documents
                </div>
                <div className="space-y-2">
                    {contracts.map(doc => (
                        <div 
                            key={doc.file_id} 
                            className={`p-2 rounded border text-sm ${
                                doc.is_active 
                                    ? 'bg-blue-50 border-blue-300' 
                                    : 'bg-gray-50 border-gray-100'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <FileText size={14} className={doc.is_active ? 'text-blue-600' : 'text-gray-400'}/>
                                    <span className="truncate text-xs">{doc.filename}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {doc.is_active ? (
                                        <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-semibold">ACTIVE</span>
                                    ) : (
                                        <button 
                                            onClick={() => setDocumentActive(doc.file_id, 'contract')}
                                            className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded hover:bg-gray-300"
                                        >
                                            Set Active
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => deleteDocument(doc.file_id, 'contract')}
                                        className="text-gray-400 hover:text-red-500 p-0.5"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            </div>
                            <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                                <Calendar size={10} />
                                {formatDate(doc.upload_date)}
                                {!doc.is_active && <span className="ml-1 text-gray-300">• Previous</span>}
                            </div>
                        </div>
                    ))}
                    {contracts.length === 0 && (
                        <div className="text-xs text-gray-400 italic p-2">No contracts uploaded</div>
                    )}
                </div>
            </div>

            {/* PROPOSAL DOCUMENTS SECTION */}
            <div className="mb-4">
                <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider flex items-center gap-2">
                    <File size={12} className="text-green-600" />
                    Proposal Documents
                </div>
                <div className="space-y-2">
                    {proposals.map(doc => (
                        <div 
                            key={doc.file_id} 
                            className={`p-2 rounded border text-sm ${
                                doc.is_active 
                                    ? 'bg-green-50 border-green-300' 
                                    : 'bg-gray-50 border-gray-100'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <File size={14} className={doc.is_active ? 'text-green-600' : 'text-gray-400'}/>
                                    <span className="truncate text-xs">{doc.filename}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {doc.is_active ? (
                                        <span className="text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded font-semibold">ACTIVE</span>
                                    ) : (
                                        <button 
                                            onClick={() => setDocumentActive(doc.file_id, 'proposal')}
                                            className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded hover:bg-gray-300"
                                        >
                                            Set Active
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => deleteDocument(doc.file_id, 'proposal')}
                                        className="text-gray-400 hover:text-red-500 p-0.5"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            </div>
                            <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                                <Calendar size={10} />
                                {formatDate(doc.upload_date)}
                                {!doc.is_active && <span className="ml-1 text-gray-300">• Previous</span>}
                            </div>
                        </div>
                    ))}
                    {proposals.length === 0 && (
                        <div className="text-xs text-gray-400 italic p-2">No proposals uploaded</div>
                    )}
                </div>
            </div>

        </div>

        {/* Upload Zones at bottom of sidebar */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 space-y-2">
            {/* Contract Upload */}
            <label className="flex flex-col items-center justify-center w-full h-14 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors bg-white">
                <div className="flex flex-col items-center justify-center">
                    <Upload className="w-4 h-4 text-blue-500 mb-0.5" />
                    <p className="text-[11px] text-blue-600 font-medium">Upload Contract</p>
                </div>
                <input type="file" className="hidden" onChange={handleContractUpload} accept=".pdf,.docx" multiple />
            </label>
            
            {/* Proposal Upload */}
            <label className="flex flex-col items-center justify-center w-full h-14 border-2 border-dashed border-green-300 rounded-lg cursor-pointer hover:bg-green-50 transition-colors bg-white">
                <div className="flex flex-col items-center justify-center">
                    <Upload className="w-4 h-4 text-green-500 mb-0.5" />
                    <p className="text-[11px] text-green-600 font-medium">Upload Proposal</p>
                </div>
                <input type="file" className="hidden" onChange={handleProposalUpload} accept=".pdf,.docx" multiple />
            </label>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Workspace */}
        <div className="flex-1 overflow-hidden relative">
            
            {/* Chat View */}
            <div className={`absolute inset-0 flex flex-col bg-white transition-opacity duration-200 ${activeTab === 'chat' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-3xl rounded-lg p-4 shadow-sm ${
                                msg.role === 'user' 
                                    ? 'bg-[hsl(var(--brand-blue))] text-white' 
                                    : 'bg-white border border-gray-200 text-gray-800'
                            }`}>
                                <div className={`prose prose-sm max-w-none ${msg.role === 'user' ? '' : 'dark:prose-invert'}`}>
                                    {msg.role === 'system' ? (
                                        <div className="flex items-center gap-2 italic text-gray-500">
                                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {msg.content}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {msg.content}
                                        </ReactMarkdown>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
                <div className="p-4 bg-white border-t border-gray-200">
                    <div className="flex gap-2 max-w-4xl mx-auto">
                        <input 
                            className="flex-1 border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                            placeholder="Ask about the contract..."
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && sendMessage()}
                            disabled={isLoading}
                        />
                        <Button onClick={sendMessage} disabled={isLoading}>
                            {isLoading ? '...' : <Play size={16} fill="currentColor" />}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Summary Tab (New) */}
            <div className={`absolute inset-0 overflow-y-auto p-8 bg-white transition-opacity duration-200 ${activeTab === 'summary' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
                {analysisResult?.structured_data?.summary_data ? (
                    <div className="max-w-4xl mx-auto">
                        <SummaryView data={analysisResult.structured_data.summary_data} />
                    </div>
                ) : (
                    <div className="flex h-full items-center justify-center text-gray-400">
                        No summary available. Run a Contract Review first.
                    </div>
                )}
            </div>

            {/* Negotiations Tab (Terms) */}
            <div className={`absolute inset-0 overflow-y-auto p-8 bg-white transition-opacity duration-200 ${activeTab === 'terms' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
                {analysisResult?.structured_data?.negotiation_summary ? (
                    <div className="max-w-4xl mx-auto">
                        <NegotiationView 
                            data={analysisResult.structured_data.negotiation_summary}
                            isNewClient={isNewClient}
                            onNewClientChange={setIsNewClient}
                        />
                    </div>
                ) : (
                    <div className="flex h-full items-center justify-center text-gray-400">
                        No negotiation items found. Run a Contract Review first.
                    </div>
                )}
            </div>

            {/* Scope Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-8 bg-white transition-opacity duration-200 ${activeTab === 'scope' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
                {analysisResult?.structured_data?.scope_data ? (
                    <div className="max-w-4xl mx-auto">
                        <div className="flex justify-end mb-4">
                            <Button 
                                onClick={runScopeReview} 
                                disabled={isLoading || !activeProposal}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                {isLoading ? 'Analyzing...' : 'Re-run Scope Review'}
                            </Button>
                        </div>
                        <ScopeView data={analysisResult.structured_data.scope_data} />
                    </div>
                ) : (
                    <div className="flex flex-col h-full items-center justify-center text-gray-400">
                        <div className="text-center max-w-md">
                            <div className="text-lg font-semibold mb-2 text-gray-700">Scope Review</div>
                            <p className="text-sm mb-4">
                                The Proposal defines what ABS priced and serves as the authoritative baseline.
                            </p>
                            
                            {/* Status indicators */}
                            <div className="mb-6 space-y-2">
                                <div className={`flex items-center justify-center gap-2 text-sm ${activeProposal ? 'text-green-600' : 'text-gray-400'}`}>
                                    {activeProposal ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                                    <span>Proposal: {activeProposal ? activeProposal.filename : 'Not uploaded'}</span>
                                </div>
                                <div className={`flex items-center justify-center gap-2 text-sm ${activeContract ? 'text-green-600' : 'text-yellow-500'}`}>
                                    {activeContract ? <CheckCircle size={16} /> : <Clock size={16} />}
                                    <span>Contract: {activeContract ? activeContract.filename : 'Optional - for comparison'}</span>
                                </div>
                            </div>
                            
                            {/* Run Scope Review Button */}
                            <Button 
                                onClick={runScopeReview} 
                                disabled={isLoading || !activeProposal}
                                className={`${activeProposal ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'}`}
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Analyzing...
                                    </span>
                                ) : (
                                    'Run Scope Review'
                                )}
                            </Button>
                            
                            {!activeProposal && (
                                <p className="text-xs text-gray-300 mt-4">
                                    Upload a Proposal document using the "Upload Proposal" button in the sidebar.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Exclusions Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-8 bg-white transition-opacity duration-200 ${activeTab === 'exclusions' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
                {analysisResult?.structured_data?.exclusions_data ? (
                    <div className="max-w-4xl mx-auto">
                        <ExclusionsView 
                            exclusionsData={analysisResult.structured_data.exclusions_data}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col h-full items-center justify-center text-gray-400">
                        <div className="text-center max-w-md">
                            <FileText size={48} className="mx-auto mb-4 opacity-50" />
                            <div className="text-lg font-semibold mb-2 text-gray-700">Proposal Exclusions</div>
                            <p className="text-sm mb-4">
                                Exclusions will be extracted from the proposal document.
                            </p>
                            <div className={`flex items-center justify-center gap-2 text-sm ${activeProposal ? 'text-green-600' : 'text-gray-400'}`}>
                                {activeProposal ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                                <span>Proposal: {activeProposal ? activeProposal.filename : 'Not uploaded'}</span>
                            </div>
                            {!activeProposal && (
                                <p className="text-xs text-gray-300 mt-4">
                                    Upload a Proposal document to extract exclusions.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Email Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-8 bg-white transition-opacity duration-200 ${activeTab === 'email' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
                <div className="max-w-4xl mx-auto">
                    <EmailView 
                        termsData={analysisResult?.structured_data?.negotiation_summary}
                        exclusionsData={analysisResult?.structured_data?.exclusions_data}
                        projectName={analysisResult?.structured_data?.summary_data?.project_name}
                        isNewClient={isNewClient}
                    />
                </div>
            </div>

            {/* History Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-8 bg-white transition-opacity duration-200 ${activeTab === 'history' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
                <div className="max-w-4xl mx-auto">
                    <HistoryView 
                        reviews={reviewHistory}
                        onLoadReview={loadReviewFromHistory}
                        onDeleteReview={deleteReviewFromHistory}
                        onRefresh={loadHistory}
                        isLoading={isHistoryLoading}
                    />
                </div>
            </div>

        </div>
      </div>
      </div>

      {/* Mira AI Feedback & Support widget — shows on all authenticated pages */}
      <MiraWidget enabled={true} />
    </div>
  );
}
